import ExplanationIcon from '@components/ExplanationIcon';
import {
  Flex,
  Grid,
  GridItem,
  Link,
  List,
  ListItem,
  SpriteIcon,
  Typography
} from '@coolblue-development/freddy';
import clickExplanationIconTrigger from '@coolblue-development/ga4-events/triggers/InsuranceInformation/clickExplanationIconTrigger';
import clickToCustomerServiceTrigger from '@coolblue-development/ga4-events/triggers/InsuranceInformation/clickToCustomerServiceTrigger';
import clickToOmbudsmanInformationTrigger from '@coolblue-development/ga4-events/triggers/InsuranceInformation/clickToOmbudsmanInformationTrigger';
import readExtendedExplanationTrigger from '@coolblue-development/ga4-events/triggers/InsuranceInformation/readExtendedExplanationTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useClientLogger } from '@coolblue-development/next-config/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import type { Insurance, InsuranceAttribute } from '@features/product/typings';
import { InsuranceProvider } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { useState } from 'react';

import { getInsuranceAttributeTrackingContent } from '../utils/getExplanationIconTrackingContent';
import { replaceInsuranceAttributePlaceholders } from '../utils/replacePlaceholders';
import AttributeDetailsModal from './AttributeDetailsModal';
import OmbudsmanModal from './OmbudsmanModal';

type Props = {
  insurance: Insurance;
  termsConditionsUrl: string;
  customerServiceUrl: string;
  insuranceCardUrl: string;
  moreInformationUrl: string;
};

export default function CoveragesBlock({
  insurance,
  insuranceCardUrl,
  customerServiceUrl,
  termsConditionsUrl,
  moreInformationUrl
}: Props) {
  const { Trans, t } = useTranslation('webshop.productPage.insurance');
  const { language, subsidiary } = useRequestContext();
  const [showOmbudsmanModal, setShowOmbudsmanModal] = useState(false);
  const [showAtrributeModal, setShowAttributeModal] = useState(false);
  const [attribute, setAttribute] = useState<InsuranceAttribute | null>(null);
  const { trackEvent } = useAnalytics();
  const clientLogger = useClientLogger();

  const showAttributeDetails = (attribute: InsuranceAttribute) => () => {
    setAttribute(
      replaceInsuranceAttributePlaceholders(
        attribute,
        insurance.summary.liability,
        language,
        subsidiary
      )
    );
    setShowAttributeModal(true);

    const trackingParamsContent = getInsuranceAttributeTrackingContent({
      iconDescription: attribute.icon
    });

    if (!trackingParamsContent) {
      clientLogger.warning(
        'Content is empty for insurance_information.click_explanation_icon tracking'
      );
      return;
    }

    trackEvent(
      clickExplanationIconTrigger({
        content: trackingParamsContent,
        sub_feature: 'product_page'
      })
    );
  };

  function handleOmbudsmanLinkClick() {
    setShowOmbudsmanModal(true);

    trackEvent(clickToOmbudsmanInformationTrigger());
  }

  function handleCustomerServiceLinkClick() {
    trackEvent(clickToCustomerServiceTrigger());
  }

  function getInsuranceDeclaration(partner: Insurance['partner']) {
    if (partner === 'CoverGenius') {
      return [
        t('insuranceDeclarationTitleCoverGenius'),
        t('insuranceDeclarationDescriptionCoverGenius')
      ];
    }

    return [t('insuranceDeclarationTitle'), t('insuranceDeclarationDescription')];
  }

  function handleExtendedExplanationUrlsClick({
    index,
    url,
    redirect
  }: {
    index: number;
    url: string;
    redirect: string;
  }) {
    trackEvent(
      readExtendedExplanationTrigger({
        sub_feature: 'product_page',
        index: index,
        url: url,
        redirect: redirect,
        total_indices: 2
      })
    );
  }

  const [insuranceDeclarationTitle, insuranceDeclarationDescription] = getInsuranceDeclaration(
    insurance.partner
  );

  return (
    <Grid templateColumns='1fr' rowGap={400}>
      <GridItem>
        <Typography variant='h3'>{t('coverageTitle')}</Typography>
        <List sx={{ width: '100%' }}>
          {insurance.summary.requiredAttributes.map(attribute => (
            <ListItem key={attribute.id}>
              <Flex
                alignItems='center'
                justifyContent={{ xs: 'space-between', md: 'flex-start' }}
                sx={{ width: '100%' }}
                inline
              >
                {attribute.name} <ExplanationIcon onClick={showAttributeDetails(attribute)} />
              </Flex>
            </ListItem>
          ))}
          {insurance.summary.optionalAttributes.map(attribute => (
            <ListItem key={attribute.id}>
              <Flex
                alignItems='center'
                justifyContent={{ xs: 'space-between', md: 'flex-start' }}
                sx={{ width: '100%' }}
                inline
              >
                <Flex inline>
                  {attribute.name}
                  <Typography ml={100} color='subtle' component='span' variant='body'>
                    ({t('optional')})
                  </Typography>
                </Flex>
                <ExplanationIcon onClick={showAttributeDetails(attribute)} />
              </Flex>
            </ListItem>
          ))}
        </List>
      </GridItem>
      {insurance.summary.conditions.length > 0 && (
        <GridItem>
          <Typography variant='h3'>{t('conditionTitle')}</Typography>
          <List>
            {insurance.summary.conditions.map(condition => (
              <ListItem key={condition.id}>{condition.description}</ListItem>
            ))}
          </List>
        </GridItem>
      )}
      <GridItem>
        <Typography variant='h3'>{insuranceDeclarationTitle}</Typography>
        <Typography>
          <Trans
            components={{
              ombudsmanLink: <Link component='button' onClick={handleOmbudsmanLinkClick} />,
              customerServiceLink: (
                <Link
                  href={customerServiceUrl}
                  onClick={handleCustomerServiceLinkClick}
                  rel='nofollow'
                  target='_blank'
                />
              )
            }}
            i18nKey={insuranceDeclarationDescription}
            t={t}
          />
        </Typography>
        {(insuranceCardUrl || termsConditionsUrl) && (
          <Grid templateColumns='auto auto 1fr' columnGap={400}>
            {insuranceCardUrl && (
              <GridItem>
                <Link
                  href={insuranceCardUrl}
                  onClick={() =>
                    handleExtendedExplanationUrlsClick({
                      url: insuranceCardUrl,
                      index: 1,
                      redirect: t('insuranceCard')
                    })
                  }
                >
                  {t('insuranceCard')}
                </Link>
              </GridItem>
            )}
            {insuranceCardUrl && termsConditionsUrl && (
              <GridItem>
                <Typography color='lightgrey'>|</Typography>
              </GridItem>
            )}
            {termsConditionsUrl && (
              <GridItem>
                {insurance.partner === InsuranceProvider.ERGO ? (
                  <Link
                    href={termsConditionsUrl}
                    onClick={() =>
                      handleExtendedExplanationUrlsClick({
                        url: termsConditionsUrl,
                        index: 2,
                        redirect: t('tnc')
                      })
                    }
                  >
                    {t('tnc')}
                  </Link>
                ) : (
                  <Link
                    href={moreInformationUrl}
                    startIcon={<SpriteIcon name='arrow-right' />}
                    target='_blank'
                    onClick={() =>
                      handleExtendedExplanationUrlsClick({
                        url: moreInformationUrl,
                        index: 2,
                        redirect: t('readMoreAboutInsurances')
                      })
                    }
                  >
                    {t('readMoreAboutInsurances')}
                  </Link>
                )}
              </GridItem>
            )}
          </Grid>
        )}
      </GridItem>
      <OmbudsmanModal open={showOmbudsmanModal} onClose={() => setShowOmbudsmanModal(false)} />
      {attribute && (
        <AttributeDetailsModal
          description={attribute.description}
          open={showAtrributeModal}
          onClose={() => setShowAttributeModal(false)}
          title={attribute.name}
        />
      )}
    </Grid>
  );
}
